import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useCallback, useRef } from 'react';
import React from 'react';
import useCMStyles from '../useCMStyles';
import { Box, IconButton, Typography } from '@material-ui/core';
import { getActiveInitRoute, getPermissions } from '../../../utils/permissions';
import usePagination from '../../../hooks/usePagination';
import {
    getAllTeamLeads,
    getTeamLeadFleets,
} from '../../../store/actions/cmActionCreator';
import {
    CM_STATUS_TYPES_MAPPING,
    convertRStoAmount,
    getColorBasedOnValue,
    preProcessPayload,
    TL_CLASS_TYPE_MAPPING,
} from '../helper.cm';
import { replaceWithUnderscore } from '../../control-center/helper';
import useCommonStyles from '../../../components/UI/CommonStyles';
import clsx from 'clsx';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CustomDropdown from '../../../components/Dropdown';
import moment from 'moment';
import {
    formatCurrencyV1,
    currencyFormatWithoutDecimal,
} from '../../../utils/appHelper';

const useTeamLead = (props: any) => {
    //values
    const containerRef = React.useRef<HTMLElement>(null);
    const initialPage = 0; // Start on page 0 (0-based index)
    const initialPageSize = 25; // Show 25 rows per page
    const initialActiveFilterCard = {
        type: 'Total',
        subType: '',
    }; // Example filter card
    const initialFilterValues = {}; // Example filter values
    const initialSearchText = ''; // Example search text
    const initialPayload = {
        pageNo: initialPage,
        pageSize: initialPageSize,
        searchText: '',
        filters: {
            ...initialFilterValues,
        },
    };

    //State values
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [containerHeight, setContainerHeight] = useState(0);
    const [teamLeadDetails, setTeamLeadDetails] = useState<any>({});
    const [tlAttendanceModalOpen, setTlAttendanceModalOpen] =
        useState<boolean>(false); // State to control the modal open/close

    //hooks
    const {
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        activeFilterCard,
        filterValues,
        searchTextValue,
        payload,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
    } = usePagination(
        initialPage,
        initialPageSize,
        initialActiveFilterCard,
        initialFilterValues,
        initialSearchText,
        initialPayload,
    );
    const { user, cm, staff, settings } = useSelector(
        (store: DefaultRootState | any) => store || {},
    );
    const dispatch = useDispatch();

    // Memoize the dispatch function to prevent recreating it on every render
    const dispatchGetTeamLeadFleets = useCallback(
        (id: string, data: any, onSuccess: Function, onError: Function) => {
            dispatch(getTeamLeadFleets(id, data, onSuccess, onError));
        },
        [dispatch],
    );

    const styles: any = useCMStyles({
        containerHeight: containerHeight,
    });
    const commonStyles: any = useCommonStyles();

    // Create a ref to track the initial render
    const initialRender = useRef(true);

    useEffect(() => {
        let permissions = getPermissions('Cluster Management');
        if (!permissions.read) {
            props.history.push(`/${getActiveInitRoute()}`);
        }
    }, [user?.configs, props?.history]);

    useEffect(() => {
        if (!!containerRef?.current?.offsetHeight && !containerHeight) {
            const height = containerRef.current.offsetHeight;
            setContainerHeight(height);
        }
    }, [containerRef?.current?.offsetHeight, containerHeight]);

    // Memoize the API call function
    const fetchTeamLeadData = useCallback(
        (id: any) => {
            setIsLoading(true);
            let preProceedPayload = preProcessPayload(payload);
            dispatchGetTeamLeadFleets(
                id,
                preProceedPayload,
                (data: any) => {
                    //setTeamLeads(data);
                    setIsLoading(false);
                },
                (error: any) => {
                    console.error('Error fetching team leads:', error);
                    setIsLoading(false);
                },
            );
        },
        [payload, dispatchGetTeamLeadFleets],
    );

    useEffect(() => {
        // Add debounce to prevent rapid API calls when payload changes
        const timer = setTimeout(() => {
            fetchTeamLeadData(props?.match?.params?.id);
        }, 300);

        return () => clearTimeout(timer);
    }, [fetchTeamLeadData, props?.match?.params?.id]);

    useEffect(() => {
        setTeamLeadDetails(props?.location?.state?.payload);
    }, [props?.location?.state?.payload]); // Add props.location?.state as dependency

    const prepareFilterCard = (card: any) => {
        switch (card?.type) {
            default:
                return (
                    <>
                        <Typography className={styles.cardHeader}>
                            {card?.name}
                        </Typography>
                        <Typography className={styles.cardSubHeader}>
                            {cm?.fleets?.[card?.valueMappingKey] ?? '0'}
                        </Typography>
                    </>
                );
        }
    };

    const actionDropDown = (val: any) => {
        let options: any = [];
        const detailsView = {
            item: 'View',
            onSuccess: (e: any) => {
                e?.stopPropagation();
                e?.preventDefault();
                if (!!val?.taggedRiderDetails?.contactNumber) {
                    handleSingleRidersPage(val);
                }
            },
            disabled: !val?.taggedRiderDetails?.contactNumber,
        };
        options = [detailsView];
        return (
            <CustomDropdown
                title={'Options'}
                options={options}
                optionStyles={null}
                expandMoreButtonStyles={null}
                styledMenuItemStyles={commonStyles.menu}
                buttonProps={null}
                buttonStyles={null}
            />
        );
    };
    const handleRowClick = (e: any, val: any) => {
        handleSingleRidersPage(val);
    };
    const getColumnComponentValue = (
        row: any,
        column: any,
        handleAction: any,
    ) => {
        let compValue: any = (
            <Box>
                <Typography className={styles.tableValue}>
                    {row?.[column?.valueKey] ?? '-/-'}
                </Typography>
            </Box>
        );
        const orderStatus: any = row?.decay
            ?.toUpperCase()
            ?.replace(' ', '_')
            ?.trim();
        const evStatus: any = row?.evLatestStatus
            ?.toUpperCase()
            ?.replace(' ', '_')
            ?.replace('-', '_')
            ?.trim()
            ?.replace(/[\s-]+/g, '_');
        const classStatus: any = replaceWithUnderscore(row?.status)
            ?.toUpperCase()
            ?.trim();
        let chipClass: any =
            commonStyles?.[`${CM_STATUS_TYPES_MAPPING?.[classStatus]}`];
        let loginClass: any =
            classStatus === 'SIGNED_IN'
                ? commonStyles.activeTxt
                : styles.tableValue;
        let chipEVClass: any = styles?.[`${TL_CLASS_TYPE_MAPPING?.[evStatus]}`];
        let chipOrderClass: any =
            styles?.[
                `${TL_CLASS_TYPE_MAPPING?.[getColorBasedOnValue(orderStatus)]}`
            ];
        switch (column?.mappingKey) {
            case 'name':
                compValue = (
                    <Box>
                        <Typography className={styles.tableValue}>
                            {row?.deName ?? '-'}
                        </Typography>
                        <Typography className={styles.tableSubValue}>
                            {row?.dePhoneNumber ?? '-'}
                        </Typography>
                    </Box>
                );
                break;
            case 'hub':
                compValue = (
                    <Box>
                        <Typography className={styles.tableValue}>
                            {row?.hubName ?? '-'}
                        </Typography>
                        <Typography className={styles.tableSubValue}>
                            {row?.cityName ?? '-'}
                        </Typography>
                    </Box>
                );
                break;
            case 'orders_today':
                compValue = (
                    <Box>
                        <Typography className={clsx(styles.tableValue)}>
                            {row?.orderCount ?? '-'}
                        </Typography>
                    </Box>
                );
                break;
            case 'wallet_balance_today':
                compValue = (
                    <Box>
                        <Typography className={clsx(styles.tableValue)}>
                            {formatCurrencyV1(
                                convertRStoAmount(`${row?.walletBalance ?? 0}`),
                                currencyFormatWithoutDecimal,
                            )}
                        </Typography>
                    </Box>
                );
                break;

            case 'decay':
                compValue = row?.decay ? (
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'flex-start'}
                        justifyContent={'flex-start'}
                    >
                        <Box
                            className={clsx([
                                commonStyles.chip,
                                chipOrderClass,
                            ])}
                        >
                            <Typography
                                component={'span'}
                                className={commonStyles.chipText}
                            >
                                {row?.decay ?? '-'}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    '-'
                );
                break;
            case 'ev_status':
                compValue = row?.evLatestStatus ? (
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'flex-start'}
                        justifyContent={'flex-start'}
                    >
                        <Box className={clsx([commonStyles.chip, chipEVClass])}>
                            <Typography
                                component={'span'}
                                className={commonStyles.chipText}
                            >
                                {row?.evLatestStatus ?? '-'}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    '-'
                );
                break;
            case 'action':
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        {/* <IconButton
                                            className={commonStyles.menu}
                                            aria-label="more"
                                            aria-controls="long-menu"
                                            aria-haspopup="true"
                                            onClick={() => {
                                                !!handleAction && handleAction('more', row);
                                            }}
                                            color="primary"
                                        >
                                            <Typography component={'span'} color="primary">
                                                {'Options'}
                                            </Typography>
                                            <MoreVertIcon />
                                        </IconButton> */}
                        <Typography className={styles.tableValue}>
                            {actionDropDown(row)}
                        </Typography>
                    </Box>
                );
                break;

            default:
                break;
        }
        return compValue;
    };

    const handleSingleRidersPage = (rider: any) => {
        if (teamLeadDetails?.userId && rider?.userId) {
            props?.history?.push(
                `/cluster-management/details/${teamLeadDetails?.userId}/rider/${rider?.userId}`,
                {
                    payload: rider,
                    backPayload: teamLeadDetails,
                },
            );
        }
    };

    const prepareFilterOptions = () => {
        return [];
    };

    const handleAttendanceModalClose = () => {
        setTlAttendanceModalOpen(false);
    };

    const handleAttendanceModalOpen = () => {
        setTlAttendanceModalOpen(true);
    };

    const classStatus: any = replaceWithUnderscore(teamLeadDetails?.status)
        ?.toUpperCase()
        ?.trim();
    let chipClass: any =
        commonStyles?.[`${CM_STATUS_TYPES_MAPPING?.[classStatus]}`];
    let loginClass: any =
        classStatus === 'SIGNED_IN'
            ? commonStyles.activeTxt
            : styles.tableValue;
    return {
        user,
        isLoading,
        styles,
        activeFilterCard,
        prepareFilterCard,
        containerRef,
        prepareFilterOptions,
        searchTextValue,
        filterValues,
        onFilterApplied,
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        getColumnComponentValue,
        cm,
        handleActiveFilterCard,
        teamLeadDetails,
        chipClass,
        commonStyles,
        loginClass,
        permissions: getPermissions('Cluster Management'),
        handleRowClick,
        tlAttendanceModalOpen,
        handleAttendanceModalOpen,
        handleAttendanceModalClose,
    };
};

export default useTeamLead;
